import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  AdjustStockRequest,
  ConfirmStockTransferRequest,
  CreateStockItemRequest,
  CreateStockTransferRequest,
  CreateUsageRequest,
  ItemCategory,
  ReceiveStockRequest,
  StockAlertResponse,
  StockBalanceResponse,
  StockItemResponse,
  StockMoveResponse,
  StockTransferResponse,
  StockUnit,
  UsageRequestResponse,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { PanelHeading } from '../money/PanelHeading';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';
import { StockCard } from './StockCard';

const CATEGORIES: readonly ItemCategory[] = [
  'CONSUMABLE',
  'SPARE_PART',
  'MEDICATION',
  'RETAIL',
  'OTHER',
];

const UNITS: readonly StockUnit[] = ['EACH', 'BOX', 'PACK', 'PAIR', 'ROLL', 'ML', 'L', 'G', 'KG'];

type Tab = 'stock' | 'requests' | 'transfers' | 'items';

/**
 * The shelf (FR-801…805, DEC-034).
 *
 * Alerts sit at the top and everything else below, because the question a branch manager opens this
 * screen with is "is anything about to stop a treatment today". A list of forty balances answers
 * that only if somebody reads all forty.
 *
 * The four tabs are the four things a shelf needs: what is on it, who has asked for some, what is in
 * a van, and the catalogue behind them all. Receiving is on the stock tab rather than a fifth one
 * because it is the same person doing it in the same minute the delivery arrives.
 */
export function InventoryPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const permissions = useAuthStore((state) => state.permissions);
  const canAdjust = permissions.has('INVENTORY_ADJUST');
  const canApprove = permissions.has('INVENTORY_APPROVE');
  const canRequest = permissions.has('INVENTORY_REQUEST');
  const canTransfer = permissions.has('INVENTORY_TRANSFER');

  const [tab, setTab] = useState<Tab>('stock');
  const [openItem, setOpenItem] = useState<string | null>(null);

  const branchKey = branchId ?? '';
  const enabled = Boolean(clinicId && branchId);

  const alerts = useApiQuery<StockAlertResponse[]>(
    queryKeys.stockAlerts(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/inventory/alerts?branchId=${branchId}`,
    { enabled },
  );
  const balances = useApiQuery<StockBalanceResponse[]>(
    queryKeys.stockBalances(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/inventory/stock?branchId=${branchId}`,
    { enabled },
  );
  const items = useApiQuery<StockItemResponse[]>(
    queryKeys.stockItems(clinicId ?? ''),
    `/clinics/${clinicId}/inventory/items`,
    { enabled: Boolean(clinicId) },
  );
  const requests = useApiQuery<UsageRequestResponse[]>(
    queryKeys.usageRequests(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/inventory/usage-requests?branchId=${branchId}`,
    { enabled: enabled && tab === 'requests' },
  );
  const outbound = useApiQuery<StockTransferResponse[]>(
    queryKeys.stockTransfers(clinicId ?? '', `out:${branchKey}`),
    `/clinics/${clinicId}/inventory/transfers?branchId=${branchId}`,
    { enabled: enabled && tab === 'transfers' },
  );
  const awaiting = useApiQuery<StockTransferResponse[]>(
    queryKeys.stockTransfers(clinicId ?? '', `awaiting:${branchKey}`),
    `/clinics/${clinicId}/inventory/transfers?branchId=${branchId}&awaiting=true`,
    { enabled: enabled && tab === 'transfers' },
  );

  /** Everything a movement touches, in one list, so no screen shows a stale number. */
  const stockKeys = [
    queryKeys.stockBalances(clinicId ?? '', branchKey),
    queryKeys.stockAlerts(clinicId ?? '', branchKey),
    queryKeys.stockItems(clinicId ?? ''),
    queryKeys.usageRequests(clinicId ?? '', branchKey),
    queryKeys.stockTransfers(clinicId ?? '', `out:${branchKey}`),
    queryKeys.stockTransfers(clinicId ?? '', `awaiting:${branchKey}`),
  ];

  const receive = useApiMutation<StockMoveResponse, ReceiveStockRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/inventory/stock/receipts`,
      options: { method: 'POST', body },
    }),
    { invalidates: stockKeys },
  );

  const adjust = useApiMutation<StockMoveResponse, AdjustStockRequest & { readonly writeOff?: boolean }>(
    ({ writeOff, ...body }) => ({
      path: `/clinics/${clinicId}/inventory/stock/${writeOff ? 'write-offs' : 'adjustments'}`,
      options: { method: 'POST', body },
    }),
    { invalidates: stockKeys },
  );

  const createItem = useApiMutation<StockItemResponse, CreateStockItemRequest>(
    (body) => ({ path: `/clinics/${clinicId}/inventory/items`, options: { method: 'POST', body } }),
    { invalidates: stockKeys },
  );

  const raise = useApiMutation<UsageRequestResponse, CreateUsageRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/inventory/usage-requests`,
      options: { method: 'POST', body },
    }),
    { invalidates: stockKeys },
  );

  const decide = useApiMutation<
    UsageRequestResponse,
    { readonly id: string; readonly outcome: 'approve' | 'reject'; readonly note?: string }
  >(
    ({ id, outcome, note }) => ({
      path: `/clinics/${clinicId}/inventory/usage-requests/${id}/${outcome}`,
      options: { method: 'POST', body: { note } },
    }),
    { invalidates: stockKeys },
  );

  const send = useApiMutation<StockTransferResponse, CreateStockTransferRequest>(
    (body) => ({ path: `/clinics/${clinicId}/inventory/transfers`, options: { method: 'POST', body } }),
    { invalidates: stockKeys },
  );

  const confirm = useApiMutation<
    StockTransferResponse,
    ConfirmStockTransferRequest & { readonly id: string }
  >(
    ({ id, ...body }) => ({
      path: `/clinics/${clinicId}/inventory/transfers/${id}/confirm`,
      options: { method: 'POST', body },
    }),
    { invalidates: stockKeys },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.inventory')} description={t('home.noClinic')} />;
  }

  const itemName = (item: { readonly nameEn?: string | null; readonly nameAr?: string | null;
    readonly itemCode?: string | null; readonly code?: string | null }) =>
    name({ nameEn: item.nameEn ?? null, nameAr: item.nameAr ?? null }, item.itemCode ?? item.code ?? '');

  return (
    <>
      <PageHeader title={t('nav.items.inventory')} description={t('inventory.description')} />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <AsyncSection query={alerts}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('inventory.alerts')} hint={t('inventory.alertsHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('inventory.noAlerts')}</p>
            ) : (
              <ul className="totals-list">
                {rows.map((alert) => (
                  <li key={`${alert.kind}:${alert.itemId}`}>
                    <span className={`pill pill--${(alert.kind ?? '').toLowerCase()}`}>
                      {t(`inventory.alertKinds.${alert.kind}`)}
                    </span>
                    <strong>{itemName(alert)}</strong>
                    <span className="muted">
                      {alert.expiresOn
                        ? t('inventory.expiresOn', { date: alert.expiresOn })
                        : t('inventory.remaining', {
                            quantity: alert.quantity,
                            unit: t(`inventory.units.${alert.unit}`),
                            minimum: alert.threshold,
                          })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </AsyncSection>

      <nav className="tabs" aria-label={t('nav.items.inventory')}>
        {(['stock', 'requests', 'transfers', 'items'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'tab tab--active' : 'tab'}
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
          >
            {t(`inventory.tabs.${key}`)}
          </button>
        ))}
      </nav>

      {tab === 'stock' ? (
        <>
          {canAdjust ? (
            <section className="panel">
              <PanelHeading title={t('inventory.receive')} hint={t('inventory.receiveHint')} />
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  receive.mutate({
                    branchId: branchId ?? '',
                    itemId: String(form.get('itemId')),
                    quantity: String(form.get('quantity')),
                    unitCost: { amount: Number(form.get('unitCost')).toFixed(2), currency: 'EGP' },
                    supplier: String(form.get('supplier') ?? '') || undefined,
                    expiresOn: String(form.get('expiresOn') ?? '') || undefined,
                    reference: String(form.get('reference') ?? '') || undefined,
                  });
                  event.currentTarget.reset();
                }}
              >
                <label className="field">
                  <span>{t('inventory.item')}</span>
                  <select name="itemId" required>
                    {(items.data ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {itemName(item)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.quantity')}</span>
                  <input name="quantity" type="number" step="0.001" min="0.001" required />
                </label>
                <label className="field">
                  <span>{t('inventory.unitCost')}</span>
                  <input name="unitCost" type="number" step="0.01" min="0" required />
                </label>
                <label className="field">
                  <span>{t('inventory.supplier')}</span>
                  <input name="supplier" />
                </label>
                <label className="field">
                  <span>{t('inventory.expiresOn')}</span>
                  <input name="expiresOn" type="date" />
                </label>
                <label className="field">
                  <span>{t('inventory.reference')}</span>
                  <input name="reference" />
                </label>
                {receive.isError ? <ErrorState error={receive.error} /> : null}
                <button type="submit" className="primary" disabled={receive.isPending}>
                  {t('inventory.receiveAction')}
                </button>
              </form>
            </section>
          ) : null}

          <AsyncSection query={balances}>
            {(rows) => (
              <section className="panel">
                <PanelHeading title={t('inventory.onHand')} />
                {rows.length === 0 ? (
                  <p className="muted">{t('inventory.nothingHeld')}</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('inventory.item')}</th>
                          <th>{t('inventory.quantity')}</th>
                          <th>{t('inventory.minimum')}</th>
                          <th>{t('inventory.inTransit')}</th>
                          <th>{t('inventory.expiresOn')}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((balance) => (
                          <tr key={balance.itemId} className={balance.belowMinimum ? 'row--warning' : undefined}>
                            <td>
                              {itemName(balance)}
                              <span className="muted"> {balance.itemCode}</span>
                            </td>
                            <td>
                              <strong>{balance.quantity}</strong>{' '}
                              <span className="muted">{t(`inventory.units.${balance.unit}`)}</span>
                            </td>
                            <td className="muted">{balance.minStock}</td>
                            <td className="muted">{balance.inTransitOut}</td>
                            <td className="muted">{balance.earliestExpiryOn ?? '—'}</td>
                            <td className="actions">
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenItem(openItem === balance.itemId ? null : (balance.itemId ?? null))
                                }
                              >
                                {t('inventory.card')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </AsyncSection>

          {openItem && branchId ? (
            <StockCard clinicId={clinicId} branchId={branchId} itemId={openItem} />
          ) : null}

          {canAdjust ? (
            <section className="panel">
              <PanelHeading title={t('inventory.adjust')} hint={t('inventory.adjustHint')} />
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  adjust.mutate({
                    branchId: branchId ?? '',
                    itemId: String(form.get('itemId')),
                    delta: String(form.get('delta')),
                    reason: String(form.get('reason')),
                    writeOff: form.get('mode') === 'writeOff',
                  });
                  event.currentTarget.reset();
                }}
              >
                <label className="field">
                  <span>{t('inventory.item')}</span>
                  <select name="itemId" required>
                    {(balances.data ?? []).map((balance) => (
                      <option key={balance.itemId} value={balance.itemId}>
                        {itemName(balance)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.mode')}</span>
                  <select name="mode" defaultValue="adjust">
                    <option value="adjust">{t('inventory.modeAdjust')}</option>
                    <option value="writeOff">{t('inventory.modeWriteOff')}</option>
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.delta')}</span>
                  <input name="delta" type="number" step="0.001" required />
                </label>
                <label className="field wide">
                  <span>{t('inventory.reason')}</span>
                  <input name="reason" required maxLength={400} />
                </label>
                {adjust.isError ? <ErrorState error={adjust.error} /> : null}
                <button type="submit" className="primary" disabled={adjust.isPending}>
                  {t('common.save')}
                </button>
              </form>
            </section>
          ) : null}
        </>
      ) : null}

      {tab === 'requests' ? (
        <>
          {canRequest ? (
            <section className="panel">
              <PanelHeading title={t('inventory.request')} hint={t('inventory.requestHint')} />
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  raise.mutate({
                    branchId: branchId ?? '',
                    itemId: String(form.get('itemId')),
                    quantity: String(form.get('quantity')),
                    purpose: String(form.get('purpose') ?? '') || undefined,
                  });
                  event.currentTarget.reset();
                }}
              >
                <label className="field">
                  <span>{t('inventory.item')}</span>
                  <select name="itemId" required>
                    {(items.data ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {itemName(item)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.quantity')}</span>
                  <input name="quantity" type="number" step="0.001" min="0.001" required />
                </label>
                <label className="field wide">
                  <span>{t('inventory.purpose')}</span>
                  <input name="purpose" />
                </label>
                {raise.isError ? <ErrorState error={raise.error} /> : null}
                <button type="submit" className="primary" disabled={raise.isPending}>
                  {t('inventory.requestAction')}
                </button>
              </form>
            </section>
          ) : null}

          <AsyncSection query={requests}>
            {(rows) => (
              <section className="panel">
                <PanelHeading title={t('inventory.requests')} />
                {rows.length === 0 ? (
                  <p className="muted">{t('inventory.noRequests')}</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('inventory.item')}</th>
                          <th>{t('inventory.quantity')}</th>
                          <th>{t('inventory.requestedBy')}</th>
                          <th>{t('sales.status')}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((request) => (
                          <tr key={request.id}>
                            <td>{itemName(request)}</td>
                            <td>
                              {request.quantity}{' '}
                              <span className="muted">{t(`inventory.units.${request.unit}`)}</span>
                            </td>
                            <td className="muted">{request.requestedByName}</td>
                            <td>
                              <span className={`pill pill--${(request.status ?? '').toLowerCase()}`}>
                                {t(`inventory.requestStatuses.${request.status}`)}
                              </span>
                            </td>
                            <td className="actions">
                              {canApprove && request.status === 'PENDING' ? (
                                <>
                                  <button
                                    type="button"
                                    className="primary"
                                    onClick={() =>
                                      decide.mutate({ id: request.id ?? '', outcome: 'approve' })
                                    }
                                  >
                                    {t('inventory.approve')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const note = window.prompt(t('inventory.rejectReason') ?? '');
                                      if (note) {
                                        decide.mutate({ id: request.id ?? '', outcome: 'reject', note });
                                      }
                                    }}
                                  >
                                    {t('inventory.reject')}
                                  </button>
                                </>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {decide.isError ? <ErrorState error={decide.error} /> : null}
              </section>
            )}
          </AsyncSection>
        </>
      ) : null}

      {tab === 'transfers' ? (
        <>
          {canTransfer ? (
            <section className="panel">
              <PanelHeading title={t('inventory.send')} hint={t('inventory.sendHint')} />
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  send.mutate({
                    fromBranchId: branchId ?? '',
                    toBranchId: String(form.get('toBranchId')),
                    itemId: String(form.get('itemId')),
                    quantity: String(form.get('quantity')),
                    note: String(form.get('note') ?? '') || undefined,
                  });
                  event.currentTarget.reset();
                }}
              >
                <label className="field">
                  <span>{t('inventory.toBranch')}</span>
                  <select name="toBranchId" required>
                    {branches
                      .filter((branch) => branch.id !== branchId)
                      .map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {name(branch, branch.code)}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.item')}</span>
                  <select name="itemId" required>
                    {(balances.data ?? []).map((balance) => (
                      <option key={balance.itemId} value={balance.itemId}>
                        {itemName(balance)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.quantity')}</span>
                  <input name="quantity" type="number" step="0.001" min="0.001" required />
                </label>
                <label className="field wide">
                  <span>{t('inventory.note')}</span>
                  <input name="note" />
                </label>
                {send.isError ? <ErrorState error={send.error} /> : null}
                <button type="submit" className="primary" disabled={send.isPending}>
                  {t('inventory.sendAction')}
                </button>
              </form>
            </section>
          ) : null}

          <AsyncSection query={awaiting}>
            {(rows) => (
              <section className="panel">
                <PanelHeading title={t('inventory.awaiting')} hint={t('inventory.awaitingHint')} />
                {rows.length === 0 ? (
                  <p className="muted">{t('inventory.nothingAwaiting')}</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('inventory.fromBranch')}</th>
                          <th>{t('inventory.item')}</th>
                          <th>{t('inventory.sent')}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((transfer) => (
                          <tr key={transfer.id}>
                            <td>{transfer.fromBranchName}</td>
                            <td>{itemName(transfer)}</td>
                            <td>{transfer.quantitySent}</td>
                            <td className="actions">
                              {canTransfer ? (
                                <button
                                  type="button"
                                  className="primary"
                                  onClick={() => {
                                    const received = window.prompt(
                                      t('inventory.confirmPrompt') ?? '',
                                      transfer.quantitySent ?? '',
                                    );
                                    if (received === null) {
                                      return;
                                    }
                                    const short = received !== transfer.quantitySent;
                                    const note = short
                                      ? window.prompt(t('inventory.discrepancyPrompt') ?? '')
                                      : null;
                                    if (short && !note) {
                                      return;
                                    }
                                    confirm.mutate({
                                      id: transfer.id ?? '',
                                      quantityReceived: received,
                                      discrepancyNote: note ?? undefined,
                                    });
                                  }}
                                >
                                  {t('inventory.confirmArrival')}
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {confirm.isError ? <ErrorState error={confirm.error} /> : null}
              </section>
            )}
          </AsyncSection>

          <AsyncSection query={outbound}>
            {(rows) => (
              <section className="panel">
                <PanelHeading title={t('inventory.sentList')} />
                {rows.length === 0 ? (
                  <p className="muted">{t('inventory.nothingSent')}</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('inventory.toBranch')}</th>
                          <th>{t('inventory.item')}</th>
                          <th>{t('inventory.sent')}</th>
                          <th>{t('inventory.received')}</th>
                          <th>{t('sales.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((transfer) => (
                          <tr key={transfer.id}>
                            <td>{transfer.toBranchName}</td>
                            <td>{itemName(transfer)}</td>
                            <td>{transfer.quantitySent}</td>
                            <td>{transfer.quantityReceived ?? '—'}</td>
                            <td>
                              <span className={`pill pill--${(transfer.status ?? '').toLowerCase()}`}>
                                {t(`inventory.transferStatuses.${transfer.status}`)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </AsyncSection>
        </>
      ) : null}

      {tab === 'items' ? (
        <>
          {canAdjust ? (
            <section className="panel">
              <PanelHeading title={t('inventory.newItem')} />
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  createItem.mutate({
                    code: String(form.get('code')),
                    nameEn: String(form.get('nameEn') ?? '') || undefined,
                    nameAr: String(form.get('nameAr') ?? '') || undefined,
                    category: String(form.get('category')) as ItemCategory,
                    unit: String(form.get('unit')) as StockUnit,
                    minStock: String(form.get('minStock') ?? '0'),
                    tracksExpiry: form.get('tracksExpiry') === 'on',
                  });
                  event.currentTarget.reset();
                }}
              >
                <label className="field">
                  <span>{t('inventory.code')}</span>
                  <input name="code" required maxLength={48} />
                </label>
                <label className="field">
                  <span>{t('common.nameEn')}</span>
                  <input name="nameEn" />
                </label>
                <label className="field">
                  <span>{t('common.nameAr')}</span>
                  <input name="nameAr" dir="rtl" />
                </label>
                <label className="field">
                  <span>{t('inventory.category')}</span>
                  <select name="category" defaultValue="CONSUMABLE">
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {t(`inventory.categories.${category}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.unit')}</span>
                  <select name="unit" defaultValue="EACH">
                    {UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {t(`inventory.units.${unit}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('inventory.minimum')}</span>
                  <input name="minStock" type="number" step="0.001" min="0" defaultValue="0" />
                </label>
                <label className="field checkbox">
                  <input name="tracksExpiry" type="checkbox" />
                  <span>{t('inventory.tracksExpiry')}</span>
                </label>
                {createItem.isError ? <ErrorState error={createItem.error} /> : null}
                <button type="submit" className="primary" disabled={createItem.isPending}>
                  {t('common.save')}
                </button>
              </form>
            </section>
          ) : null}

          <AsyncSection query={items}>
            {(rows) => (
              <section className="panel">
                <PanelHeading title={t('inventory.catalogue')} hint={t('inventory.catalogueHint')} />
                {rows.length === 0 ? (
                  <p className="muted">{t('inventory.noItems')}</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('inventory.code')}</th>
                          <th>{t('inventory.item')}</th>
                          <th>{t('inventory.category')}</th>
                          <th>{t('inventory.minimum')}</th>
                          <th>{t('inventory.totalOnHand')}</th>
                          <th>{t('inventory.lastCost')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((item) => (
                          <tr key={item.id}>
                            <td>{item.code}</td>
                            <td>{itemName(item)}</td>
                            <td>{t(`inventory.categories.${item.category}`)}</td>
                            <td className="muted">{item.minStock}</td>
                            <td>{item.totalOnHand}</td>
                            <td className="muted">{item.lastUnitCost ? money(item.lastUnitCost) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </AsyncSection>
        </>
      ) : null}
    </>
  );
}
